import React from "react";
import { Link } from "react-router-dom";
import SeoMeta from "../SeoMeta";
import { FaRocket, FaBriefcase, FaUserFriends, FaGraduationCap, FaArrowRight, FaHeart } from "react-icons/fa";
import { useTheme } from "../Theme/ThemeContext";

export default function About() {
  const { currentTheme } = useTheme();
  const pageUrl = "https://www.code-arena.in/about";
  const description =
    "CodeArena helps developers explore job opportunities, internships, and referrals to kickstart their tech careers.";

  const features = [
    {
      icon: FaBriefcase,
      title: "Job Listings",
      description: "Explore curated job opportunities from top tech companies",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: FaGraduationCap,
      title: "Internships",
      description: "Find the perfect internship to kickstart your career",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      icon: FaUserFriends,
      title: "Referrals",
      description: "Leverage referral opportunities from industry professionals",
      gradient: "from-orange-500 to-red-500"
    },
    {
      icon: FaRocket,
      title: "Skill Building",
      description: "Access courses and resources to enhance your technical skills",
      gradient: "from-green-500 to-emerald-500"
    }
  ];

  return (
    <>
      {/* ✅ SEO Meta */}
      <SeoMeta
        title="About Us | CodeArena"
        description={description}
        url={pageUrl}
        image="/logo192.png"
        keywords="CodeArena, About, Developer Community, Jobs, Internships, Referrals, Tech Careers"
        type="website"
      />

      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:from-neutral-900 dark:via-neutral-900 dark:to-neutral-800">
        {/* Hero Section */}
        <div className={`relative bg-gradient-to-r ${currentTheme.colors.gradient} py-20 overflow-hidden`}>
          {/* Decorative elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-10 left-10 w-64 h-64 bg-white/10 rounded-full filter blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-400/20 rounded-full filter blur-3xl"></div>
          </div>
          
          <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
              <FaHeart className="w-4 h-4 text-red-400" />
              <span className="text-white text-sm font-medium">Our Mission</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              About <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-200">CodeArena</span>
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Empowering developers to find their dream careers and enhance their technical skills
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-5xl mx-auto px-4 py-16">
          {/* Story Section */}
          <div className="glass rounded-3xl shadow-xl border border-white/20 dark:border-white/10 p-8 sm:p-12 mb-12">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-6 flex items-center gap-3">
                <div className={`w-10 h-10 bg-gradient-to-br ${currentTheme.colors.gradient} rounded-xl flex items-center justify-center shadow-lg ${currentTheme.colors.glow}`}>
                  <FaRocket className="w-5 h-5 text-white" />
                </div>
                Our Story
              </h2>
              
              <div className="space-y-5 text-neutral-600 dark:text-neutral-300 leading-relaxed">
                <p>
                  Welcome to <span className="font-semibold text-accent-600">CodeArena</span> – 
                  a platform dedicated to helping developers find jobs, internships, and referrals, 
                  while enhancing their technical skills.
                </p>

                <p>
                  At CodeArena, you can explore curated job listings, apply for internships, 
                  and leverage referral opportunities from industry professionals. Our aim is 
                  to bridge the gap between learning and career growth.
                </p>

                <p>
                  Build your profile, connect with mentors, and access resources that help 
                  you land the right job or internship in the tech industry.
                </p>

                <p>
                  Join our growing community of developers, collaborate with peers, and take 
                  the next step in your tech career confidently.
                </p>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-white text-center mb-8">What We Offer</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="glass rounded-2xl p-6 border border-white/20 dark:border-white/10 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group"
                >
                  <div className={`w-12 h-12 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-neutral-900 dark:text-white mb-2">{feature.title}</h3>
                  <p className="text-neutral-600 dark:text-neutral-300">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link
              to="/"
              className={`inline-flex items-center gap-2 bg-gradient-to-r ${currentTheme.colors.gradient} hover:opacity-90 text-white font-semibold py-3.5 px-8 rounded-xl transition-all duration-300 shadow-lg ${currentTheme.colors.glow} hover:shadow-xl hover:scale-[1.02]`}
            >
              Explore CodeArena
              <FaArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
